import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Lock, Settings, Save, Loader2, CheckCircle, TrendingUp, TrendingDown,
  Clock, Trash2, ChevronDown, ChevronUp, Plus, Minus, Building2, Globe,
  Coins, Banknote, RotateCcw, ShoppingCart, Printer, CheckCircle2,
  AlertCircle, Info,
} from 'lucide-react';
import { formatDateTime, formatDateTimeShort, formatDayTime } from '../../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const COIN_ROLLS = [
  { key: '200', label: '2,00 €-Rolle',  rollValue: 50.00 },
  { key: '100', label: '1,00 €-Rolle',  rollValue: 25.00 },
  { key: '50',  label: '0,50 €-Rolle',  rollValue: 20.00 },
  { key: '20',  label: '0,20 €-Rolle',  rollValue:  8.00 },
  { key: '10',  label: '0,10 €-Rolle',  rollValue:  4.00 },
  { key: '5',   label: '0,05 €-Rolle',  rollValue:  2.50 },
  { key: '2',   label: '0,02 €-Rolle',  rollValue:  1.00 },
  { key: '1',   label: '0,01 €-Rolle',  rollValue:  0.50 },
];
const NOTES_DEF = [
  { key: '100', label: '100 €-Schein', value: 100 },
  { key: '50',  label: '50 €-Schein',  value: 50  },
  { key: '20',  label: '20 €-Schein',  value: 20  },
  { key: '10',  label: '10 €-Schein',  value: 10  },
  { key: '5',   label: '5 €-Schein',   value: 5   },
];
const LOOSE_COINS_DEF = [
  { key: '200', label: '2,00 €-Münze', value: 2.00 },
  { key: '100', label: '1,00 €-Münze', value: 1.00 },
  { key: '50',  label: '0,50 €-Münze', value: 0.50 },
];

const EMPTY_ROLLS       = Object.fromEntries(COIN_ROLLS.map(r => [r.key, 0]));
const EMPTY_NOTES       = Object.fromEntries(NOTES_DEF.map(n => [n.key, 0]));
const EMPTY_LOOSE_COINS = Object.fromEntries(LOOSE_COINS_DEF.map(c => [c.key, 0]));

const fmt = (val) => {
  if (val === null || val === undefined) return '–';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val);
};

const calcTotal = (rolls) =>
  COIN_ROLLS.reduce((s, r) => s + (rolls[r.key] || 0) * r.rollValue, 0);

// ─── Bankverbindung: IBAN-Helfer ──────────────────────────────────────────────
const BANK_MODES = ['iban', 'kontonummer', 'frei'];
const BANK_MODE_LABEL = { iban: 'IBAN', kontonummer: 'Kontonummer', frei: 'Frei' };
const normalizeIban = (v) => (v || '').replace(/\s+/g, '').toUpperCase();
const isValidDeIban = (v) => /^DE\d{20}$/.test(normalizeIban(v));
// Vereinfachte Ableitung: letzte 10 Ziffern der IBAN, führende Nullen entfernt.
const deriveAccountFromIban = (v) => {
  const n = normalizeIban(v);
  if (!/^DE\d{20}$/.test(n)) return '';
  return n.slice(-10).replace(/^0+/, '') || '0';
};
const formatIbanDisplay = (v) => {
  const n = normalizeIban(v);
  return n.replace(/(.{4})/g, '$1 ').trim();
};

// ─── Delta-Badge ──────────────────────────────────────────────────────────────
const DeltaBadge = ({ delta, large = false }) => {
  if (delta === null || delta === undefined) return null;
  const base = large
    ? 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold'
    : 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold';
  if (Math.abs(delta) < 0.01) return (
    <span className={`${base} bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300`} data-testid="delta-exact">
      <CheckCircle className={large ? 'h-4 w-4' : 'h-3 w-3'} /> Passt genau
    </span>
  );
  if (delta > 0) return (
    <span className={`${base} bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300`} data-testid="delta-plus">
      <TrendingUp className={large ? 'h-4 w-4' : 'h-3 w-3'} /> +{fmt(Math.abs(delta))} über Soll
    </span>
  );
  return (
    <span className={`${base} bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400`} data-testid="delta-minus">
      <TrendingDown className={large ? 'h-4 w-4' : 'h-3 w-3'} /> {fmt(Math.abs(delta))} unter Soll
    </span>
  );
};

// ─── Zähler-Zeile ─────────────────────────────────────────────────────────────
const CounterRow = ({ label, unitLabel, count, onChange, subtotal }) => (
  <div className="flex items-center gap-2 py-3 border-b border-gray-100 dark:border-gray-800/60 last:border-0" data-testid="counter-row">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 leading-tight">{label}</p>
      <p className="text-xs text-gray-400 mt-0.5">{unitLabel}</p>
    </div>
    <div className="flex items-center gap-1.5 flex-shrink-0">
      <button type="button" onClick={() => onChange(Math.max(0, count - 1))} disabled={count <= 0}
        className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-800 active:bg-gray-200 dark:active:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 disabled:opacity-25 transition-colors touch-manipulation" aria-label="Minus">
        <Minus className="h-4 w-4" />
      </button>
      <span className="w-10 text-center text-base font-bold tabular-nums text-gray-900 dark:text-white select-none">{count}</span>
      <button type="button" onClick={() => onChange(count + 1)}
        className="w-11 h-11 rounded-xl bg-primary/10 dark:bg-primary/20 active:bg-primary/20 dark:active:bg-primary/30 flex items-center justify-center text-primary transition-colors touch-manipulation" aria-label="Plus">
        <Plus className="h-4 w-4" />
      </button>
    </div>
    <div className="w-20 text-right flex-shrink-0">
      <span className={`text-sm font-bold tabular-nums ${subtotal > 0 ? 'text-primary' : 'text-gray-300 dark:text-gray-600'}`}>
        {fmt(subtotal)}
      </span>
    </div>
  </div>
);

// ─── Accordion-Block ──────────────────────────────────────────────────────────
const AccordionBlock = ({ icon: Icon, title, blockTotal, isOpen, onToggle, onReset, children }) => (
  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden" data-testid={`block-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <button type="button" onClick={onToggle}
      className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left">
      <div className="w-8 h-8 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-800 dark:text-white">{title}</p>
        <p className={`text-xs font-semibold tabular-nums ${blockTotal > 0 ? 'text-primary' : 'text-gray-400'}`}>{fmt(blockTotal)}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {blockTotal > 0 && (
          <button type="button" onClick={(e) => { e.stopPropagation(); onReset(); }}
            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-colors" title="Block zurücksetzen" data-testid={`reset-${title}`}>
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        )}
        {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </div>
    </button>
    {isOpen && <div className="px-4 pb-2 border-t border-gray-100 dark:border-gray-800">{children}</div>}
  </div>
);

// ─── Bankbedarf Komponente ─────────────────────────────────────────────────────
const BankBedarf = ({ locationId, refreshKey = 0, onOrderChanged }) => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [isOpen, setIsOpen]     = useState(false);
  const [saving, setSaving]     = useState(false);
  const [completing, setCompleting] = useState(false);
  const [openOrder, setOpenOrder]   = useState(null);
  const [lastCompleted, setLastCompleted] = useState(null); // zuletzt erledigter Auftrag
  const [pdfLoading, setPdfLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = locationId
        ? `${API}/tresor/bank-need?location_id=${locationId}`
        : `${API}/tresor/bank-need`;
      const [needRes, ordersRes] = await Promise.all([
        axios.get(url, { headers: authHeader() }),
        axios.get(locationId ? `${API}/tresor/bank-orders?location_id=${locationId}` : `${API}/tresor/bank-orders`, { headers: authHeader() }),
      ]);
      setData(needRes.data);
      const orders = ordersRes.data || [];
      const open = orders.find(o => o.status === 'open') || null;
      setOpenOrder(open);

      // Letzter erledigter Auftrag: prüfen ob er von heute ist
      const completed = orders.find(o => o.status === 'completed') || null;
      if (completed && !open) {
        const completedDate = new Date(completed.completed_at || completed.created_at);
        const now = new Date();
        const diffHours = (now - completedDate) / (1000 * 60 * 60);
        // Zeige "Erledigt"-Status wenn Auftrag in den letzten 24h abgeschlossen wurde
        setLastCompleted(diffHours < 24 ? completed : null);
      } else {
        setLastCompleted(null);
      }
    } catch (err) {
      console.error('[BankBedarf] Ladefehler:', err?.response?.status, err?.message);
    } finally { setLoading(false); }
  }, [locationId]);

  useEffect(() => { load(); }, [load, refreshKey]);  // refreshKey erzwingt Reload bei neuer Zählung

  const handleSaveOrder = async () => {
    if (!data?.has_need) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/tresor/bank-orders`, {
        location_id: locationId || null,
        items: data.items,
        total_rolls_value: data.total_rolls_value,
        total_bank_fee: data.total_bank_fee,
        grand_total: data.grand_total,
        based_on_entry_id: data.last_entry?.id,
      }, { headers: authHeader() });
      setOpenOrder(res.data);
      toast.success('Bankauftrag gespeichert');
      onOrderChanged && onOrderChanged(); // Parent informieren → BankBedarf-Refresh via refreshKey
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Fehler beim Speichern';
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const handleComplete = async () => {
    if (!openOrder) return;
    setCompleting(true);
    try {
      await axios.put(
        `${API}/tresor/bank-orders/${openOrder.id}/complete`,
        {},
        { headers: authHeader() }
      );
      toast.success('Münzen als geholt markiert!');
      setOpenOrder(null);
      load(); // Sofort neu laden damit BankBedarf den aktuellen State zeigt
      onOrderChanged && onOrderChanged();
      // Direkt als erledigt setzen ohne auf load() zu warten → sofortige UI-Reaktion
      setLastCompleted({ ...openOrder, completed_at: new Date().toISOString(), completed_by_name: 'Du' });
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Unbekannter Fehler';
      toast.error(`Fehler: ${msg}`);
    } finally { setCompleting(false); }
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      const url = locationId
        ? `${API}/tresor/bank-need/pdf?location_id=${locationId}`
        : `${API}/tresor/bank-need/pdf`;
      const res = await axios.get(url, { headers: authHeader(), responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const objUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objUrl;
      link.download = `Muenzrollen-Einkauf.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(objUrl); }, 5000);
    } catch { toast.error('PDF-Fehler'); }
    finally { setPdfLoading(false); }
  };

  const noNeed = data && !data.has_need && data.reason !== 'no_target';

  const isAwaitingCount = lastCompleted && !openOrder; // Auftrag abgeschlossen, noch keine neue Zählung

  return (
    <div className={`bg-white dark:bg-gray-900 rounded-xl overflow-hidden border ${
      isAwaitingCount
        ? 'border-gray-200 dark:border-gray-700'
        : 'border-amber-200 dark:border-amber-800/50'
    }`} data-testid="bank-bedarf">
      {/* Header */}
      <button type="button" onClick={() => setIsOpen(v => !v)}
        className={`w-full flex items-center gap-3 px-4 py-3.5 transition-colors text-left ${
          isAwaitingCount
            ? 'bg-gray-50 dark:bg-gray-800/40 hover:bg-gray-100 dark:hover:bg-gray-800/60'
            : 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30'
        }`}>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isAwaitingCount
            ? 'bg-gray-100 dark:bg-gray-700/60'
            : 'bg-amber-100 dark:bg-amber-800/40'
        }`}>
          {isAwaitingCount
            ? <CheckCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            : <ShoppingCart className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-800 dark:text-white">
            {isAwaitingCount ? 'Münzrollen-Bedarf' : 'Bankbedarf berechnen'}
          </p>
          <p className={`text-xs font-medium ${isAwaitingCount ? 'text-gray-400 dark:text-gray-500' : 'text-amber-600 dark:text-amber-400'}`}>
            {isAwaitingCount
              ? 'Zählung ausstehend — Auftrag abgeschlossen'
              : loading ? 'Lädt…' : data?.has_need ? `${fmt(data.grand_total)} zur Bank` : data?.reason === 'no_target' ? 'Kein Sollwert konfiguriert' : 'Kein Bedarf'}
          </p>
        </div>
        {openOrder && (
          <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full font-semibold flex-shrink-0">
            Offen
          </span>
        )}
        {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>

      {isOpen && (
        <div className="p-4 space-y-4 border-t border-amber-100 dark:border-amber-800/30">
          {loading ? (
            <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
          ) : data?.reason === 'no_target' ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
              <Info className="h-4 w-4 flex-shrink-0" />
              Bitte zuerst einen Sollwert konfigurieren.
            </div>
          ) : lastCompleted && !openOrder ? (
            /* ─── Erledigt-Zustand ─── */
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/50 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-800/40 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-green-800 dark:text-green-300">Münzen wurden geholt</p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                    {lastCompleted.completed_by_name} · {lastCompleted.completed_at
                      ? new Date(lastCompleted.completed_at).toLocaleString('de-DE', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })
                      : 'Gerade eben'}
                  </p>
                </div>
                <span className="text-sm font-bold text-green-700 dark:text-green-300 tabular-nums">
                  {fmt(lastCompleted.grand_total)}
                </span>
              </div>
              <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/30 rounded-xl">
                <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  <span className="font-semibold">Nächster Schritt:</span> Bitte eine neue Tresorzählung durchführen, damit das System die neuen Münzen erfasst und der Bedarf verschwindet.
                </p>
              </div>
              <button
                onClick={() => setLastCompleted(null)}
                className="w-full text-xs text-gray-400 hover:text-gray-600 py-1 transition-colors"
              >
                Trotzdem Bankbedarf anzeigen
              </button>
            </div>
          ) : noNeed ? (
            <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              Alle Münzrollen sind vollständig. Kein Bankgang nötig.
            </div>
          ) : !data ? (
            /* Fehler-Zustand: API hat keine Daten geliefert */
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              Bankbedarf konnte nicht geladen werden. Bitte Sollwert konfigurieren und eine Tresorzählung durchführen.
            </div>
          ) : (
            <>
              {/* Basierend auf */}
              {data?.last_entry && (
                <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-50 dark:bg-gray-800/40 rounded-lg px-3 py-2">
                  <Info className="h-3.5 w-3.5 flex-shrink-0" />
                  Basierend auf Zählung von <span className="font-semibold text-gray-600 dark:text-gray-300 mx-1">{data.last_entry.created_by_name}</span>
                  {data.last_entry.handed_over_by_name && <span>· Übergabe: {data.last_entry.handed_over_by_name}</span>}
                </div>
              )}

              {/* Fehlende Rollen Tabelle */}
              <div>
                <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Fehlende Münzrollen</p>
                <div className="space-y-1">
                  {data.items.map(item => (
                    <div key={item.key} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-800/40 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-red-600 dark:text-red-400 w-5">{item.needed}×</span>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.label}</span>
                        <span className="text-xs text-gray-400">({fmt(item.roll_value)}/Rolle)</span>
                      </div>
                      <span className="text-sm font-bold text-gray-800 dark:text-white tabular-nums">{fmt(item.subtotal)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Zusammenfassung */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Münzrollen gesamt</span>
                  <span className="font-semibold tabular-nums">{fmt(data.total_rolls_value)}</span>
                </div>
                {data.bank_fee_per_roll > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      Bankgebühren ({data.total_rolls_needed} × {fmt(data.bank_fee_per_roll)})
                    </span>
                    <span className="font-semibold tabular-nums text-orange-600">{fmt(data.total_bank_fee)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold border-t border-amber-200 dark:border-amber-700/50 pt-2 mt-1">
                  <span>Gesamtbetrag zur Bank</span>
                  <span className="text-amber-700 dark:text-amber-300 tabular-nums">{fmt(data.grand_total)}</span>
                </div>
              </div>

              {/* Tresor-Bestand */}
              <div>
                <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Aktuell im Tresor</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Scheine', value: data.current_notes_total },
                    { label: 'Kleingeld', value: data.current_loose_total },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-gray-50 dark:bg-gray-800/40 rounded-lg p-2 text-center">
                      <p className="text-xs text-gray-400">{label}</p>
                      <p className="text-sm font-bold text-gray-800 dark:text-white tabular-nums">{fmt(value)}</p>
                    </div>
                  ))}
                  <div className="bg-gray-50 dark:bg-gray-800/40 rounded-lg p-2 text-center">
                    <p className="text-xs text-gray-400">Gesamt Ist</p>
                    <p className="text-sm font-bold text-primary tabular-nums">{fmt((data.last_entry?.actual_value) || 0)}</p>
                  </div>
                </div>
              </div>

              {/* Aktions-Buttons */}
              <div className="flex gap-2 pt-1">
                <button onClick={handlePdf} disabled={pdfLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                  data-testid="print-pdf-btn">
                  {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                  Zettel drucken
                </button>
                {!openOrder ? (
                  <button onClick={handleSaveOrder} disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                    data-testid="save-order-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Auftrag speichern
                  </button>
                ) : (
                  <button onClick={handleComplete} disabled={completing}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                    data-testid="complete-order-btn">
                    {completing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Münzen wurden geholt
                  </button>
                )}
              </div>

              {/* Offener Auftrag Hinweis */}
              {openOrder && (
                <div className="flex items-center gap-2 text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 rounded-lg px-3 py-2">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                  Auftrag offen seit {new Date(openOrder.created_at).toLocaleDateString('de-DE')} · {fmt(openOrder.grand_total)}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Tresor-Zählung ───────────────────────────────────────────────────────────
const TresorZaehlung = ({ target, locations, onEntrySaved, defaultLocationId = null }) => {
  const [selectedLocId, setSelectedLocId] = useState(defaultLocationId);
  const [currentTarget, setCurrentTarget] = useState(target);
  const [coinRolls, setCoinRolls]     = useState({ ...EMPTY_ROLLS });
  const [noteCounts, setNoteCounts]   = useState({ ...EMPTY_NOTES });
  const [looseCoins, setLooseCoins]   = useState({ ...EMPTY_LOOSE_COINS });
  const [noteText, setNoteText]       = useState('');
  const [handedOverBy, setHandedOverBy] = useState('');
  const [handoverError, setHandoverError] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [submitting, setSubmitting]   = useState(false);
  const [open, setOpen] = useState({ rolls: true, notes: true, coins: true });

  const currentUserId = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').id || ''; }
    catch { return ''; }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const url = selectedLocId
          ? `${API}/tresor/target?location_id=${selectedLocId}`
          : `${API}/tresor/target`;
        const res = await axios.get(url, { headers: authHeader() });
        if (!res.data?.target_value && selectedLocId) {
          const global = await axios.get(`${API}/tresor/target`, { headers: authHeader() });
          setCurrentTarget(global.data);
        } else {
          setCurrentTarget(res.data);
        }
      } catch { /* use passed target */ }
    };
    load();
  }, [selectedLocId]);

  useEffect(() => { setCurrentTarget(target); }, [target]);

  useEffect(() => {
    axios.get(`${API}/tresor/team`, { headers: authHeader() })
      .then(res => setTeamMembers(res.data || []))
      .catch(() => {});
  }, []);

  const rollsTotal = useMemo(() =>
    COIN_ROLLS.reduce((s, r) => s + (coinRolls[r.key] || 0) * r.rollValue, 0), [coinRolls]);
  const notesTotal = useMemo(() =>
    NOTES_DEF.reduce((s, n) => s + (noteCounts[n.key] || 0) * n.value, 0), [noteCounts]);
  const coinsTotal = useMemo(() =>
    LOOSE_COINS_DEF.reduce((s, c) => s + (looseCoins[c.key] || 0) * c.value, 0), [looseCoins]);
  const grandTotal = rollsTotal + notesTotal + coinsTotal;
  const targetVal = currentTarget?.target_value;
  const delta = targetVal !== null && targetVal !== undefined ? grandTotal - targetVal : null;
  const hasEntries = rollsTotal > 0 || notesTotal > 0 || coinsTotal > 0;

  const handleSubmit = async () => {
    if (!hasEntries) { toast.error('Bitte mindestens einen Wert eingeben'); return; }
    if (!handedOverBy) { setHandoverError(true); toast.error('Bitte "Schicht übernommen von" auswählen'); return; }
    setHandoverError(false);
    setSubmitting(true);
    try {
      const isFruehschicht = handedOverBy === '__fruehschicht__';
      const res = await axios.post(`${API}/tresor/entries`, {
        actual_value: grandTotal,
        note: noteText,
        location_id: selectedLocId,
        breakdown: { coin_rolls: coinRolls, notes: noteCounts, loose_coins: looseCoins },
        handed_over_by_user_id: isFruehschicht ? null : handedOverBy,
        handed_over_by_label: isFruehschicht ? 'Frühschicht' : null,
      }, { headers: authHeader() });
      onEntrySaved(res.data);
      setCoinRolls({ ...EMPTY_ROLLS });
      setNoteCounts({ ...EMPTY_NOTES });
      setLooseCoins({ ...EMPTY_LOOSE_COINS });
      setNoteText('');
      setHandedOverBy('');
      toast.success('Zählung gespeichert');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <Lock className="h-4 w-4 text-primary" /> Tresor zählen
        </h2>
        {targetVal !== null && targetVal !== undefined && (
          <span className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-lg text-gray-500 dark:text-gray-400" data-testid="target-label">
            Soll: {fmt(targetVal)}
          </span>
        )}
      </div>

      {locations.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="station-selector">
          <button onClick={() => setSelectedLocId(null)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border font-semibold transition-all ${selectedLocId === null ? 'bg-primary text-white border-primary' : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            data-testid="station-all"><Globe className="h-3 w-3" /> Alle</button>
          {locations.map(loc => (
            <button key={loc.id} onClick={() => setSelectedLocId(loc.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border font-semibold transition-all ${selectedLocId === loc.id ? 'bg-primary text-white border-primary' : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              data-testid={`station-${loc.id}`}><Building2 className="h-3 w-3" /> {loc.name}</button>
          ))}
        </div>
      )}

      <AccordionBlock icon={Coins} title="Münzrollen" blockTotal={rollsTotal}
        isOpen={open.rolls} onToggle={() => setOpen(p => ({ ...p, rolls: !p.rolls }))}
        onReset={() => setCoinRolls({ ...EMPTY_ROLLS })}>
        {COIN_ROLLS.map(roll => (
          <CounterRow key={roll.key} label={roll.label} unitLabel={`${fmt(roll.rollValue)} / Rolle`}
            count={coinRolls[roll.key] || 0}
            onChange={val => setCoinRolls(p => ({ ...p, [roll.key]: val }))}
            subtotal={(coinRolls[roll.key] || 0) * roll.rollValue} />
        ))}
      </AccordionBlock>

      <AccordionBlock icon={Banknote} title="Scheine" blockTotal={notesTotal}
        isOpen={open.notes} onToggle={() => setOpen(p => ({ ...p, notes: !p.notes }))}
        onReset={() => setNoteCounts({ ...EMPTY_NOTES })}>
        {NOTES_DEF.map(note => (
          <CounterRow key={note.key} label={note.label} unitLabel={`${fmt(note.value)} / Schein`}
            count={noteCounts[note.key] || 0}
            onChange={val => setNoteCounts(p => ({ ...p, [note.key]: val }))}
            subtotal={(noteCounts[note.key] || 0) * note.value} />
        ))}
      </AccordionBlock>

      <AccordionBlock icon={Coins} title="Kleingeld" blockTotal={coinsTotal}
        isOpen={open.coins} onToggle={() => setOpen(p => ({ ...p, coins: !p.coins }))}
        onReset={() => setLooseCoins({ ...EMPTY_LOOSE_COINS })}>
        {LOOSE_COINS_DEF.map(coin => (
          <CounterRow key={coin.key} label={coin.label} unitLabel={`${fmt(coin.value)} / Münze`}
            count={looseCoins[coin.key] || 0}
            onChange={val => setLooseCoins(p => ({ ...p, [coin.key]: val }))}
            subtotal={(looseCoins[coin.key] || 0) * coin.value} />
        ))}
      </AccordionBlock>

      <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)}
        placeholder="Notiz (optional) – z.B. Abendkasse, Sondereinlage …"
        maxLength={200}
        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50"
        data-testid="note-input" />

      <div className="space-y-1.5">
        <label className={`text-xs font-semibold uppercase tracking-wide ${handoverError ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
          Schicht übernommen von <span className="text-red-500">*</span>
        </label>
        <select value={handedOverBy}
          onChange={e => { setHandedOverBy(e.target.value); setHandoverError(false); }}
          className={`w-full px-4 py-3 border rounded-xl text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 appearance-none cursor-pointer transition-colors ${handoverError ? 'border-red-400 dark:border-red-500 focus:ring-red-400/50' : 'border-gray-200 dark:border-gray-700 focus:ring-primary/50'}`}
          data-testid="handover-select">
          <option value="">– Bitte auswählen –</option>
          <optgroup label="Schicht"><option value="__fruehschicht__">Frühschicht</option></optgroup>
          {teamMembers.filter(m => m.id !== currentUserId).length > 0 && (
            <optgroup label="Mitarbeiter">
              {teamMembers.filter(m => m.id !== currentUserId).map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </optgroup>
          )}
        </select>
        {handoverError && <p className="text-xs text-red-500 mt-1">Pflichtfeld – bitte eine Auswahl treffen</p>}
      </div>

      <div className="sticky bottom-0 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700" data-testid="sticky-bar">
        <div className="flex items-center justify-between mb-2 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex gap-3">
            <span>Rollen: <b className="text-gray-700 dark:text-gray-300">{fmt(rollsTotal)}</b></span>
            <span>Scheine: <b className="text-gray-700 dark:text-gray-300">{fmt(notesTotal)}</b></span>
            <span>KG: <b className="text-gray-700 dark:text-gray-300">{fmt(coinsTotal)}</b></span>
          </div>
        </div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">Gesamt</p>
            <p className="text-2xl font-black tabular-nums text-gray-900 dark:text-white" data-testid="grand-total">{fmt(grandTotal)}</p>
          </div>
          <DeltaBadge delta={delta} large />
        </div>
        <button onClick={handleSubmit} disabled={submitting || !hasEntries || !handedOverBy}
          className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-primary text-white rounded-xl text-sm font-bold hover:bg-primary/90 active:bg-primary/80 disabled:opacity-40 transition-colors touch-manipulation"
          data-testid="submit-entry-btn">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {submitting ? 'Wird gespeichert …' : 'Zählung speichern'}
        </button>
      </div>
    </div>
  );
};

// ─── Sollwert-Konfigurator ─────────────────────────────────────────────────────
const TargetConfigurator = ({ locations, onSaved }) => {
  const [selectedLocId, setSelectedLocId] = useState(null);
  const [coinRolls, setCoinRolls] = useState({ ...EMPTY_ROLLS });
  const [bankFee, setBankFee]     = useState('');
  const [bankMode, setBankMode]     = useState('iban');
  const [bankIban, setBankIban]     = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankFree, setBankFree]     = useState('');
  const [loadingTarget, setLoadingTarget] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [isOpen, setIsOpen]   = useState(false);

  const loadTarget = useCallback(async (locId) => {
    setLoadingTarget(true);
    try {
      const url = locId ? `${API}/tresor/target?location_id=${locId}` : `${API}/tresor/target`;
      const res = await axios.get(url, { headers: authHeader() });
      setCoinRolls({ ...EMPTY_ROLLS, ...(res.data?.coin_rolls || {}) });
      setBankFee(res.data?.bank_fee_per_roll != null ? String(res.data.bank_fee_per_roll) : '');
      setBankMode(res.data?.bank_mode || 'iban');
      setBankIban(res.data?.bank_iban ? formatIbanDisplay(res.data.bank_iban) : '');
      setBankAccount(res.data?.bank_account || '');
      setBankFree(res.data?.bank_free || '');
      setSavedAt(res.data?.updated_at || null);
    } catch { toast.error('Fehler beim Laden der Sollwert-Konfiguration'); }
    finally { setLoadingTarget(false); }
  }, []);

  useEffect(() => { loadTarget(selectedLocId); }, [selectedLocId, loadTarget]);

  const total = calcTotal(coinRolls);

  const derivedAccount = useMemo(
    () => (bankMode === 'iban' && isValidDeIban(bankIban) ? deriveAccountFromIban(bankIban) : ''),
    [bankMode, bankIban]
  );

  const cycleBankMode = () => {
    setBankMode(prev => BANK_MODES[(BANK_MODES.indexOf(prev) + 1) % BANK_MODES.length]);
  };

  const handleSave = async () => {
    // Grundlegende IBAN-Format-Validierung im IBAN-Modus
    if (bankMode === 'iban' && normalizeIban(bankIban) && !isValidDeIban(bankIban)) {
      toast.error('Ungültige IBAN. Erwartet: DE + 20 Ziffern (z.B. DE12 3456 7890 1234 5678 90).');
      return;
    }
    setSaving(true);
    try {
      const feeVal = bankFee !== '' ? parseFloat(bankFee.replace(',', '.')) : null;
      const bankPayload =
        bankMode === 'iban'
          ? { bank_mode: 'iban', bank_iban: normalizeIban(bankIban), bank_account: derivedAccount, bank_free: '' }
          : bankMode === 'kontonummer'
          ? { bank_mode: 'kontonummer', bank_iban: '', bank_account: bankAccount.trim(), bank_free: '' }
          : { bank_mode: 'frei', bank_iban: '', bank_account: '', bank_free: bankFree.trim() };
      const res = await axios.put(`${API}/tresor/target`,
        { coin_rolls: coinRolls, location_id: selectedLocId, bank_fee_per_roll: isNaN(feeVal) ? null : feeVal, ...bankPayload },
        { headers: authHeader() }
      );
      toast.success(`Sollwert ${fmt(res.data.target_value)} gespeichert`);
      setSavedAt(res.data.updated_at);
      onSaved(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const selectedName = selectedLocId
    ? locations.find(l => l.id === selectedLocId)?.name || 'Station'
    : 'Alle Stationen';

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden" data-testid="target-configurator">
      <button type="button" onClick={() => setIsOpen(v => !v)}
        className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-primary" />
          <span className="text-sm font-bold text-gray-800 dark:text-white">Sollwert konfigurieren</span>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary uppercase tracking-wide">Admin</span>
        </div>
        <div className="flex items-center gap-2">
          {savedAt && <span className="text-xs text-gray-400 hidden sm:block">{formatDateTimeShort(savedAt)}</span>}
          {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </button>

      {isOpen && (
        <div className="p-5 space-y-4">
          {locations.length > 1 && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Konfiguration für</label>
              <div className="flex flex-wrap gap-2" data-testid="config-station-selector">
                <button onClick={() => setSelectedLocId(null)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border font-semibold transition-all ${selectedLocId === null ? 'bg-primary text-white border-primary' : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                  data-testid="config-station-all"><Globe className="h-3 w-3" /> Alle Stationen</button>
                {locations.map(loc => (
                  <button key={loc.id} onClick={() => setSelectedLocId(loc.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border font-semibold transition-all ${selectedLocId === loc.id ? 'bg-primary text-white border-primary' : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                    data-testid={`config-station-${loc.id}`}><Building2 className="h-3 w-3" /> {loc.name}</button>
                ))}
              </div>
            </div>
          )}

          {loadingTarget ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : (
            <div>
              <div className="flex items-center gap-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                <p className="flex-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">Münzrolle</p>
                <p className="w-28 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide">Anzahl</p>
                <p className="w-20 text-right text-xs font-semibold text-gray-400 uppercase tracking-wide">Betrag</p>
              </div>
              {COIN_ROLLS.map(roll => (
                <CounterRow key={roll.key} label={roll.label} unitLabel={`${fmt(roll.rollValue)} / Rolle`}
                  count={coinRolls[roll.key] || 0}
                  onChange={val => setCoinRolls(p => ({ ...p, [roll.key]: val }))}
                  subtotal={(coinRolls[roll.key] || 0) * roll.rollValue} />
              ))}
            </div>
          )}

          {/* Bankgebühr */}
          <div className="border border-amber-200 dark:border-amber-700/50 rounded-xl p-4 bg-amber-50 dark:bg-amber-900/10 space-y-2">
            <label className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wide flex items-center gap-1.5">
              <ShoppingCart className="h-3.5 w-3.5" />
              Bankgebühr pro Münzrolle (optional)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.01"
                min="0"
                max="5"
                value={bankFee}
                onChange={e => setBankFee(e.target.value)}
                placeholder="z.B. 0,30"
                className="flex-1 px-3 py-2 border border-amber-200 dark:border-amber-700/50 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                data-testid="bank-fee-input"
              />
              <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">€ / Rolle</span>
            </div>
            <p className="text-xs text-amber-600 dark:text-amber-500">
              Wird für die Bankgebühren-Berechnung im Einkaufsplan verwendet.
            </p>
          </div>

          {/* Bankverbindung der Station */}
          <div className="border border-blue-200 dark:border-blue-700/50 rounded-xl p-4 bg-blue-50 dark:bg-blue-900/10 space-y-2" data-testid="bank-account-block">
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs font-bold text-blue-700 dark:text-blue-400 uppercase tracking-wide flex items-center gap-1.5">
                <Banknote className="h-3.5 w-3.5" />
                Bankverbindung ({selectedName})
              </label>
              <button
                type="button"
                onClick={cycleBankMode}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 bg-white dark:bg-gray-900 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                data-testid="bank-mode-toggle"
                title="Eingabemodus umschalten"
              >
                <RotateCcw className="h-3 w-3" />
                {BANK_MODE_LABEL[bankMode]}
              </button>
            </div>

            {bankMode === 'iban' && (
              <div className="space-y-1.5">
                <input
                  type="text"
                  value={bankIban}
                  onChange={e => setBankIban(e.target.value)}
                  placeholder="DE12 3456 7890 1234 5678 90"
                  className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50 ${normalizeIban(bankIban) && !isValidDeIban(bankIban) ? 'border-red-400 dark:border-red-500' : 'border-blue-200 dark:border-blue-700/50'}`}
                  data-testid="bank-iban-input"
                />
                {normalizeIban(bankIban) && !isValidDeIban(bankIban) && (
                  <p className="text-xs text-red-500" data-testid="bank-iban-error">Ungültige IBAN – erwartet: DE + 20 Ziffern.</p>
                )}
                {derivedAccount && (
                  <p className="text-xs text-blue-600 dark:text-blue-400" data-testid="derived-account">
                    Abgeleitete Kontonummer: <b className="tabular-nums">{derivedAccount}</b>
                  </p>
                )}
              </div>
            )}

            {bankMode === 'kontonummer' && (
              <input
                type="text"
                value={bankAccount}
                onChange={e => setBankAccount(e.target.value)}
                placeholder="Kontonummer"
                className="w-full px-3 py-2 border border-blue-200 dark:border-blue-700/50 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50"
                data-testid="bank-account-input"
              />
            )}

            {bankMode === 'frei' && (
              <input
                type="text"
                value={bankFree}
                onChange={e => setBankFree(e.target.value)}
                placeholder="Freie Eingabe (Bank / Kontoangabe)"
                className="w-full px-3 py-2 border border-blue-200 dark:border-blue-700/50 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50"
                data-testid="bank-free-input"
              />
            )}

            <p className="text-xs text-blue-600 dark:text-blue-500">
              Erscheint als Info im PDF (Bankgebühren-Einzug). Klicke auf den Button, um zwischen IBAN, Kontonummer und Frei umzuschalten.
            </p>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-gray-700 dark:text-gray-300">Gesamtsollwert</span>
              <span className={`text-xl font-black tabular-nums ${total > 0 ? 'text-primary' : 'text-gray-400'}`} data-testid="total-display">
                {fmt(total)}
              </span>
            </div>
            <button onClick={handleSave} disabled={saving || loadingTarget}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-xl text-sm font-bold hover:bg-primary/90 disabled:opacity-50 transition-colors"
              data-testid="save-target-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Wird gespeichert …' : `Sollwert speichern (${selectedName})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Hauptseite ───────────────────────────────────────────────────────────────
// ── HistoryEntry: Aufklappbare Verlauf-Zeile (Stückelung nur für Admin/SL) ────
function HistoryEntry({ entry, idx, isConfigurator, hasBreakdown, rolls, notes, looseCoins, rollsTotal, notesTotal, looseTotal, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div data-testid={`history-entry-${idx}`}>
      {/* Zusammenfassungs-Zeile */}
      <div className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/30 group">
        {/* Expand-Pfeil nur für Admin/SL UND wenn Stückelungsdaten vorhanden */}
        {isConfigurator && hasBreakdown ? (
          <button
            onClick={() => setExpanded(v => !v)}
            className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex-shrink-0 transition-colors"
            data-testid={`expand-entry-${idx}`}
            title="Stückelung anzeigen"
          >
            {expanded
              ? <ChevronUp className="h-3.5 w-3.5" />
              : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        ) : (
          /* Platzhalter damit Layout gleich bleibt */
          <span className="w-5 flex-shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-900 dark:text-white">{fmt(entry.actual_value)}</span>
            <DeltaBadge delta={entry.delta} />
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-gray-400">Soll: {fmt(entry.target_value)}</span>
            {entry.note && <span className="text-xs text-gray-500">· "{entry.note}"</span>}
            {entry.handed_over_by_name && (
              <span className="text-xs text-blue-500 dark:text-blue-400">
                · Übernommen von: {entry.handed_over_by_name}
              </span>
            )}
          </div>
        </div>

        <div className="text-right flex-shrink-0">
          <p className="text-xs text-gray-400">{formatDayTime(entry.created_at)}</p>
          <p className="text-xs text-gray-400">{entry.created_by_name}</p>
        </div>

        {isConfigurator && (
          <button onClick={() => onDelete(entry.id)}
            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-300 hover:text-red-500 transition-all flex-shrink-0"
            data-testid={`delete-entry-${idx}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Aufgeklappte Stückelung — NUR für Admin/SL */}
      {isConfigurator && expanded && hasBreakdown && (
        <div className="px-10 pb-4 pt-1 bg-gray-50 dark:bg-gray-800/30 border-t border-gray-100 dark:border-gray-800"
          data-testid={`entry-breakdown-${idx}`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">

            {/* Münzrollen */}
            {COIN_ROLLS.some(r => (rolls[r.key] || 0) > 0) && (
              <div>
                <p className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                  Münzrollen <span className="normal-case font-normal">{fmt(rollsTotal)}</span>
                </p>
                <div className="space-y-0.5">
                  {COIN_ROLLS.filter(r => (rolls[r.key] || 0) > 0).map(r => (
                    <div key={r.key} className="flex justify-between text-gray-600 dark:text-gray-300">
                      <span>{rolls[r.key]}× {r.label}</span>
                      <span className="text-gray-400">{fmt(rolls[r.key] * r.rollValue)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Scheine */}
            {NOTES_DEF.some(n => (notes[n.key] || 0) > 0) && (
              <div>
                <p className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                  Scheine <span className="normal-case font-normal">{fmt(notesTotal)}</span>
                </p>
                <div className="space-y-0.5">
                  {NOTES_DEF.filter(n => (notes[n.key] || 0) > 0).map(n => (
                    <div key={n.key} className="flex justify-between text-gray-600 dark:text-gray-300">
                      <span>{notes[n.key]}× {n.label}</span>
                      <span className="text-gray-400">{fmt(notes[n.key] * n.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Einzelmünzen */}
            {LOOSE_COINS_DEF.some(c => (looseCoins[c.key] || 0) > 0) && (
              <div>
                <p className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                  Einzelmünzen <span className="normal-case font-normal">{fmt(looseTotal)}</span>
                </p>
                <div className="space-y-0.5">
                  {LOOSE_COINS_DEF.filter(c => (looseCoins[c.key] || 0) > 0).map(c => (
                    <div key={c.key} className="flex justify-between text-gray-600 dark:text-gray-300">
                      <span>{looseCoins[c.key]}× {c.label}</span>
                      <span className="text-gray-400">{fmt(looseCoins[c.key] * c.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Gesamt-Zeile */}
          <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700 flex gap-4 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
            <span>Rollen: <strong className="text-gray-700 dark:text-gray-200">{fmt(rollsTotal)}</strong></span>
            <span>Scheine: <strong className="text-gray-700 dark:text-gray-200">{fmt(notesTotal)}</strong></span>
            <span>KG: <strong className="text-gray-700 dark:text-gray-200">{fmt(looseTotal)}</strong></span>
            <span className="ml-auto">Gesamt: <strong className="text-gray-900 dark:text-white">{fmt(rollsTotal + notesTotal + looseTotal)}</strong></span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TresorPage() {
  const [locations, setLocations]   = useState([]);
  const [globalTarget, setGlobalTarget] = useState(null);
  const [entries, setEntries]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activeLocationId, setActiveLocationId] = useState(null); // Aktiver Standort für BankBedarf

  const userStr  = localStorage.getItem('user');
  const userRole = userStr ? (JSON.parse(userStr).role || '') : '';
  const isConfigurator = ['admin', 'stationsleitung'].includes(userRole);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [locsRes, targetRes, entriesRes] = await Promise.all([
        axios.get(`${API}/user/locations`, { headers: authHeader() }),
        axios.get(`${API}/tresor/target`, { headers: authHeader() }),
        axios.get(`${API}/tresor/entries`, { headers: authHeader() }),
      ]);
      const locs = locsRes.data || [];
      setLocations(locs);
      setGlobalTarget(targetRes.data);
      setEntries(entriesRes.data || []);
      // Wenn genau 1 Standort → automatisch setzen für BankBedarf
      if (locs.length === 1) {
        setActiveLocationId(locs[0].id);
      }
    } catch { toast.error('Fehler beim Laden der Tresor-Daten'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const [bankBedarfRefreshKey, setBankBedarfRefreshKey] = useState(0);

  const handleTargetSaved = (data) => {
    if (!data.location_id) setGlobalTarget(data);
    setBankBedarfRefreshKey(k => k + 1); // Sollwert-Änderung → BankBedarf neu laden
  };

  const handleEntrySaved = (entry) => {
    setEntries(prev => [entry, ...prev].slice(0, 30));
    setBankBedarfRefreshKey(k => k + 1); // Neue Zählung → BankBedarf neu laden
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/tresor/entries/${entryId}`, { headers: authHeader() });
      setEntries(prev => prev.filter(e => e.id !== entryId));
      toast.success('Eintrag gelöscht');
    } catch { toast.error('Fehler beim Löschen'); }
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 max-w-2xl mx-auto pb-8">
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Lock className="h-6 w-6 text-primary" /> Tresor
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Ist-Wert erfassen, Bankbedarf berechnen
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            {isConfigurator && (
              <TargetConfigurator locations={locations} onSaved={handleTargetSaved} />
            )}

            {/* Bankbedarf – für alle sichtbar, mit Standort-Auswahl */}
            {locations.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setActiveLocationId(null)}
                  className={`px-3 py-1.5 rounded-lg text-xs border font-semibold transition-all ${activeLocationId === null ? 'bg-amber-500 text-white border-amber-500' : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50'}`}
                >
                  Alle Standorte
                </button>
                {locations.map(loc => (
                  <button key={loc.id}
                    onClick={() => setActiveLocationId(loc.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs border font-semibold transition-all ${activeLocationId === loc.id ? 'bg-amber-500 text-white border-amber-500' : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50'}`}
                  >
                    {loc.name}
                  </button>
                ))}
              </div>
            )}
            <BankBedarf
              locationId={activeLocationId}
              refreshKey={bankBedarfRefreshKey}
              onOrderChanged={() => setBankBedarfRefreshKey(k => k + 1)}
            />

            <TresorZaehlung
              target={globalTarget}
              locations={locations}
              onEntrySaved={handleEntrySaved}
              defaultLocationId={activeLocationId}
            />

            {entries.length > 0 && (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden mt-2" data-testid="history-section">
                <button onClick={() => setHistoryOpen(v => !v)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  data-testid="toggle-history-btn">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Verlauf ({entries.length})</span>
                  </div>
                  {historyOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </button>
                {historyOpen && (
                  <div className="border-t border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
                    {entries.map((entry, idx) => {
                      const bd = entry.breakdown || {};
                      const rolls = bd.coin_rolls || {};
                      const notes = bd.notes || {};
                      const looseCoins = bd.loose_coins || {};
                      const hasBreakdown = Object.values(rolls).some(v => v > 0)
                        || Object.values(notes).some(v => v > 0)
                        || Object.values(looseCoins).some(v => v > 0);
                      const rollsTotal = COIN_ROLLS.reduce((s, r) => s + (rolls[r.key] || 0) * r.rollValue, 0);
                      const notesTotal = NOTES_DEF.reduce((s, n) => s + (notes[n.key] || 0) * n.value, 0);
                      const looseTotal = LOOSE_COINS_DEF.reduce((s, c) => s + (looseCoins[c.key] || 0) * c.value, 0);

                      return (
                        <HistoryEntry
                          key={entry.id}
                          entry={entry}
                          idx={idx}
                          isConfigurator={isConfigurator}
                          hasBreakdown={hasBreakdown}
                          rolls={rolls} notes={notes} looseCoins={looseCoins}
                          rollsTotal={rollsTotal} notesTotal={notesTotal} looseTotal={looseTotal}
                          onDelete={handleDeleteEntry}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
